/**
 * My payslips: the employee's own pay. No ability check on purpose: `/api/me/payslips`
 * reads the person off the session and takes no employee id, so it cannot show anyone else.
 * Issued runs only, enforced by the server.
 */
import { Download, Eye, EyeOff, FileText, X } from 'lucide-react'
import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { errorMessage } from '../../lib/api'
import { downloadFile } from '../../lib/download'
import { holidayCredit, isLedgerOnly } from '../../lib/frozenSegment'
import { formatMoney, formatMoneyOrDash } from '../../lib/money'
import {
  myPayslipPdfUrl,
  recordedList,
  recordedMap,
  useMyPayslip,
  type ExcludedDay,
  type FrozenSegment,
  type MyPayslip,
  type PayRun,
  type Recorded,
} from '../../lib/payroll'
import { masked, periodQuery, useMyPayslipsInPeriod, type PeriodMode } from '../../lib/me'
import { netNotRecorded } from '../../lib/salaryPeriods'
import { amountOrNoteLine, clYearEndLine, clYearEndValue, labelWithNote, payNoteText } from './clYearEndLine'
import { TaskPayLines } from './TaskPayLines'
import { MyLeavePayoutHistory } from '../time/LeavePayoutRequest'
import { defaultPayslipLang, type PayslipLang } from './payslipLang'
import { PayslipLangPicker } from './PayslipLangPicker'
import { MyOneOffPayslips } from './OneOffPayslips'
import { HelpTip } from '../../components/ui/HelpTip'
import { sortableHeader } from '../../lib/sortableHeader'
import { nextSort, sortRows, type SortState, type SortValue } from '../../lib/tableSort'
import {
  Button,
  dialogScrollBodyClass,
  dialogScrollPanelClass,
  EmptyState,
  ErrorState,
  IconButton,
  LoadingState,
  ModalBackdrop,
  mutedTextClass,
  ResponsiveTable,
  type ResponsiveTableColumn,
  TextInput,
  Notice,
} from '../../components/ui'

export function monthLabel(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`
}

export type PayslipMonth = {
  year: number
  month: number
  advance: MyPayslip | null
  final: MyPayslip | null
}

function byMonth(rows: MyPayslip[]): PayslipMonth[] {
  const months = new Map<string, PayslipMonth>()
  for (const row of rows) {
    const key = monthLabel(row.year, row.month)
    let entry = months.get(key)
    if (!entry) {
      entry = { year: row.year, month: row.month, advance: null, final: null }
      months.set(key, entry)
    }
    if (row.run === 'advance') entry.advance = row
    else entry.final = row
  }
  return [...months.values()]
}

/** One run's payable, doubling as the button that opens it. An unissued run shows a dash, never a zero. */
function RunCell({
  month,
  slip,
  runLabel,
  onOpen,
  shown,
}: {
  month: PayslipMonth
  slip: MyPayslip | null
  runLabel: string
  onOpen: (slip: MyPayslip) => void
  /** While amounts are hidden the cell is the mask and not a button; a missing run is masked too so paid months are not revealed. */
  shown: boolean
}) {
  const { t } = useTranslation()
  if (!shown) {
    return <span className="tabular-nums text-slate-500 dark:text-slate-400">{masked(false, '')}</span>
  }
  if (!slip) {
    return (
      <span className="tabular-nums text-slate-500 dark:text-slate-400">
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t('payroll.runNotIssued')}</span>
      </span>
    )
  }
  const payable = runPayable(month, slip)
  return (
    <button
      type="button"
      onClick={() => onOpen(slip)}
      // The visible text is a bare number, so period and run go in the accessible name.
      aria-label={t('payroll.viewRunPayslip', {
        period: monthLabel(month.year, month.month),
        run: runLabel,
      })}
      className="rounded-lg px-1.5 py-1 tabular-nums text-slate-800 underline decoration-slate-300 underline-offset-4 hover:bg-slate-50 hover:decoration-slate-500 dark:text-slate-100 dark:decoration-slate-600 dark:hover:bg-slate-700/60"
    >
      <FileText className="mr-1 inline h-3.5 w-3.5 opacity-60" />
      {/* A gross-only month's payable is net-derived, so the control that
          opens the slip says "View" instead of showing it. @see runPayable */}
      {payable == null ? t('payroll.viewPayslip') : formatMoney(payable)}
    </button>
  )
}

export type MyPayslipSortKey = 'period' | 'advance' | 'final' | 'total'

/** One run's payable as shown, or null for an unissued run or a gross-only month (its net is unreliable, so the payable derived from it is hidden). */
export function runPayable(month: PayslipMonth, slip: MyPayslip | null): number | null {
  if (!slip || isGrossOnlyPeriod(month.year, month.month)) return null
  if (netNotRecorded(slip)) return null
  return Number(slip.payable)
}

/** A month summed over a run whose net was never recorded: the sum is unknown, not 0. */
function monthNetNotRecorded(month: PayslipMonth): boolean {
  return [month.advance, month.final].some((run) => !!run && netNotRecorded(run))
}

/** Sort value resolver at module scope for testing; unissued runs resolve to null so they sort last, and total stays numeric except for gross-only months. */
export function payslipSortValue(month: PayslipMonth, key: MyPayslipSortKey): SortValue {
  switch (key) {
    case 'period':
      return monthLabel(month.year, month.month)
    case 'advance':
      return runPayable(month, month.advance)
    case 'final':
      return runPayable(month, month.final)
    case 'total':
      if (isGrossOnlyPeriod(month.year, month.month) || monthNetNotRecorded(month)) return null
      return Number(month.advance?.payable ?? 0) + Number(month.final?.payable ?? 0)
  }
}

/** Paid-total cell: a dash only when both runs are absent, one issued run totals itself; gross-only months dash too. */
export function paidTotalDisplay(month: PayslipMonth): string {
  if (!month.advance && !month.final) return '—'
  if (isGrossOnlyPeriod(month.year, month.month) || monthNetNotRecorded(month)) return '—'
  return formatMoney(Number(month.advance?.payable ?? 0) + Number(month.final?.payable ?? 0))
}

/**
 * Payslips up to 2025-12 show gross only. Compared by (year, month) because pre-boundary
 * rows have no cut_off date; December 2025 is the last gross-only month.
 */
// Twin of `GROSS_ONLY_THROUGH` in `services/payslip.py`; change both or neither.
const GROSS_ONLY_THROUGH = { year: 2025, month: 12 } as const

/** Fails closed: an unrecognisable year/month hides the breakdown. */
export function isGrossOnlyPeriod(
  year: number | null | undefined,
  month: number | null | undefined,
): boolean {
  if (year == null || month == null || !Number.isFinite(year) || !Number.isFinite(month)) return true
  return (
    year < GROSS_ONLY_THROUGH.year ||
    (year === GROSS_ONLY_THROUGH.year && month <= GROSS_ONLY_THROUGH.month)
  )
}

/**
 * ⛔ Gross, social and tax are cumulative month-to-date, so never sum the two runs: pick the later issued run.
 * Null when neither run is issued. social/tax are also null when `gross > 0 && net === 0` (lost on import) or the month is gross-only.
 */
export function monthFigure(month: PayslipMonth, key: 'gross' | 'social' | 'tax'): number | null {
  const source = month.final ?? month.advance
  if (!source) return null
  if (key !== 'gross') {
    if (netNotRecorded(source)) return null
    if (isGrossOnlyPeriod(month.year, month.month)) return null
  }
  return Number(source[key])
}

/**
 * Lines of the gross block. The deduction sits above gross because it is a component of it. With `custom_gross`
 * the block ends on one line set by payroll, and bonus, deduction and allowances are kept but marked not applied.
 */
type GrossLedgerKey =
  | 'allowances'
  | 'bonus'
  | 'deduction'
  | 'gross'
  | 'grossSetByPayroll'
  | 'leavePayout'
  | 'breakClPsl'
  | 'alSettlement'
type GrossLedgerLine = {
  key: GrossLedgerKey
  amount: number
  sign: '' | '−'
  strong: boolean
  applied: boolean
}
/** Literal `t()` calls so `i18n/keys.test.ts` sees every key. */
function grossLedgerLabel(key: GrossLedgerKey, t: (key: string) => string): string {
  switch (key) {
    case 'allowances':
      return t('payroll.allowances')
    case 'bonus':
      return t('payroll.bonus')
    case 'deduction':
      return t('payroll.deduction')
    case 'gross':
      return t('payroll.gross')
    case 'grossSetByPayroll':
      return t('payroll.grossSetByPayroll')
    case 'leavePayout':
      return t('payroll.leavePayoutOfWhich')
    case 'breakClPsl':
      return t('payroll.breakClPslPay')
    case 'alSettlement':
      return t('payroll.alSettlementOfWhich')
  }
}
/** The after-tax one-offs that ride on payable below net, in the PDF's order; a zero has no line. */
export function afterTaxLines(data: {
  activity_bonus?: number | string | null
  break_al_pay?: number | string | null
}): { key: 'activityBonus' | 'breakAl'; amount: number; sign: '+' | '−' }[] {
  const lines = [
    { key: 'activityBonus' as const, value: Number(data.activity_bonus ?? 0) },
    { key: 'breakAl' as const, value: Number(data.break_al_pay ?? 0) },
  ]
  return lines
    .filter((line) => line.value !== 0)
    .map((line) => ({ key: line.key, amount: Math.abs(line.value), sign: line.value > 0 ? '+' : '−' }))
}
function afterTaxLabel(key: 'activityBonus' | 'breakAl', t: (key: string) => string): string {
  return key === 'activityBonus' ? t('payroll.activityBonus') : t('payroll.breakAlPay')
}
/** Whether payroll set gross by hand; zero is a set override, only null is not. */
/** The payslip dialog top to bottom: what was paid, the totals, then the days nothing paid. */
export const PAYSLIP_SECTIONS = ['work', 'segments', 'annualLeave', 'totals', 'notPaid'] as const
type PayslipSection = (typeof PAYSLIP_SECTIONS)[number]

/** The segments whose span can hold unpaid days; a ledger-only segment has no span. */
export function notPaidSegments(segments: FrozenSegment[] | null): FrozenSegment[] {
  return (segments ?? []).filter((segment) => !isLedgerOnly(segment))
}

export function grossOverridden(data: { custom_gross: number | string | null }): boolean {
  return data.custom_gross != null
}
export function grossLedger(data: {
  allowance_value: number | string
  bonus: number | string
  deduction: number | string
  gross: number | string
  custom_gross: number | string | null
  leave_payout?: number | string
  break_cl_psl_pay?: number | string
  break_cl_psl_note?: string | null
  al_settlement_pay?: number | string
}): GrossLedgerLine[] {
  const overridden = grossOverridden(data)
  const lines: GrossLedgerLine[] = [
    { key: 'allowances', amount: Number(data.allowance_value), sign: '', strong: false, applied: !overridden },
  ]
  if (Number(data.bonus) !== 0) {
    lines.push({ key: 'bonus', amount: Number(data.bonus), sign: '', strong: false, applied: !overridden })
  }
  if (Number(data.deduction) !== 0) {
    lines.push({
      key: 'deduction',
      amount: Number(data.deduction),
      sign: overridden ? '' : '−',
      strong: false,
      applied: !overridden,
    })
  }
  const payout = Number(data.leave_payout ?? 0)
  const breakPay = Number(data.break_cl_psl_pay ?? 0)
  const alSettlement = Number(data.al_settlement_pay ?? 0)
  const ofWhich: GrossLedgerLine[] = []
  if (payout !== 0) {
    ofWhich.push({ key: 'leavePayout', amount: Math.abs(payout), sign: payout < 0 ? '−' : '', strong: false, applied: true })
  }
  if (amountOrNoteLine(breakPay, data.break_cl_psl_note)) {
    ofWhich.push({ key: 'breakClPsl', amount: Math.abs(breakPay), sign: breakPay < 0 ? '−' : '', strong: false, applied: true })
  }
  if (alSettlement !== 0) {
    ofWhich.push({ key: 'alSettlement', amount: Math.abs(alSettlement), sign: alSettlement < 0 ? '−' : '', strong: false, applied: true })
  }
  // The server adds every settlement on top of a payroll-set gross, so with either one the block still ends on `gross`.
  if (overridden) {
    lines.push({
      key: 'grossSetByPayroll',
      amount: Number(data.custom_gross),
      sign: '',
      strong: ofWhich.length === 0,
      applied: true,
    })
  }
  if (!overridden || ofWhich.length > 0) {
    lines.push({ key: 'gross', amount: Number(data.gross), sign: '', strong: true, applied: true })
  }
  return [...lines, ...ofWhich]
}

/** A figure and its label as a tile. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-base font-medium tabular-nums text-slate-900 dark:text-slate-100">
        {value}
      </dd>
    </div>
  )
}

/**
 * `allowance_value` itemised across the frozen segments. Keys are server-written labels, rendered as-is.
 * A null `allowance_detail` contributes nothing, and no itemisation renders no panel. With `overridden` a line says none of it entered the gross.
 */
function AllowanceBreakdown({ segments, overridden }: { segments: FrozenSegment[] | null; overridden: boolean }) {
  const { t } = useTranslation()
  const totals = (segments ?? []).reduce<Record<string, number>>((acc, segment) => {
    for (const [mode, value] of Object.entries(segment.allowance_detail ?? {})) {
      acc[mode] = (acc[mode] ?? 0) + Number(value)
    }
    return acc
  }, {})
  const entries = Object.entries(totals)
  if (entries.length === 0) return null
  return (
    <section className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {t('payroll.allowances')}
      </h3>
      <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        {entries.map(([mode, total]) => (
          <Line key={mode} label={mode} value={formatMoney(total)} />
        ))}
      </dl>
      {overridden && <p className={`mt-2 ${mutedTextClass}`}>{t('payroll.allowancesNotApplied')}</p>}
    </section>
  )
}

export default function MyPayslips() {
  const { t } = useTranslation()
  // EMPTY BY DEFAULT: a null period fetches nothing. Amounts hidden by default, in component state on purpose, so every visit starts hidden.
  const [mode, setMode] = useState<PeriodMode>('month')
  const [monthPick, setMonthPick] = useState('')
  const [fromPick, setFromPick] = useState('')
  const [toPick, setToPick] = useState('')
  const [shown, setShown] = useState(false)
  const period = periodQuery(mode, monthPick, fromPick, toPick)
  const backwards = mode === 'range' && !!fromPick && !!toPick && period == null
  const { data, isLoading, isError, error, refetch, isFetching } = useMyPayslipsInPeriod(period)
  const [open, setOpen] = useState<MyPayslip | null>(null)
  const [sort, setSort] = useState<SortState<MyPayslipSortKey>>(null)

  function toggleShown() {
    // Hiding also closes an open payslip: it is nothing but figures.
    if (shown) setOpen(null)
    setShown(!shown)
  }

  const months = byMonth(period ? (data ?? []) : [])
  // The server's month order is only the default; `sortRows` returns `months` unchanged while nothing is sorted.
  const sortedMonths = sortRows(months, sort, payslipSortValue)
  const advanceLabel = t('payroll.run_advance')
  const finalLabel = t('payroll.run_final')

  function payslipHeader(key: MyPayslipSortKey, label: string) {
    return sortableHeader({
      sortKey: key,
      label,
      ariaLabel: t('admin.sortBy', { column: label }),
      sort,
      onSort: (k) => setSort((current) => nextSort(current, k)),
    })
  }

  const monthColumns: ResponsiveTableColumn<PayslipMonth>[] = [
    {
      key: 'period',
      header: payslipHeader('period', t('payroll.period')),
      cell: (row) => monthLabel(row.year, row.month),
    },
    // Read through `monthFigure`, which picks the later issued run; summing would overstate the month.
    {
      key: 'gross',
      header: t('payroll.gross'),
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums text-slate-700 dark:text-slate-200">
          {masked(shown, formatMoneyOrDash(monthFigure(row, 'gross')))}
        </span>
      ),
    },
    {
      key: 'social',
      header: t('payroll.social'),
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums text-slate-700 dark:text-slate-200">
          {masked(shown, formatMoneyOrDash(monthFigure(row, 'social')))}
        </span>
      ),
    },
    {
      key: 'tax',
      header: t('payroll.tax'),
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums text-slate-700 dark:text-slate-200">
          {masked(shown, formatMoneyOrDash(monthFigure(row, 'tax')))}
        </span>
      ),
    },
    {
      key: 'advance',
      header: payslipHeader('advance', advanceLabel),
      align: 'right',
      cell: (row) => (
        <RunCell month={row} slip={row.advance} runLabel={advanceLabel} onOpen={setOpen} shown={shown} />
      ),
    },
    {
      key: 'final',
      header: payslipHeader('final', finalLabel),
      align: 'right',
      cell: (row) => (
        <RunCell month={row} slip={row.final} runLabel={finalLabel} onOpen={setOpen} shown={shown} />
      ),
    },
    {
      key: 'total',
      header: payslipHeader('total', t('payroll.paidTotal')),
      align: 'right',
      // `payable` on a final run already has the advance deducted, so this is a sum. Not labelled as what reached the bank. Neither run issued shows a dash.
      cell: (row) => (
        <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
          {masked(shown, paidTotalDisplay(row))}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('app.mySalary')}
        </h1>
        <HelpTip label={t('app.mySalary')}>
          <span className="block">{t('me.salaryHint')}</span>
          <span className="mt-1 block">{t('payroll.monthFigureHint')}</span>
        </HelpTip>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div role="radiogroup" aria-label={t('payroll.period')} className="inline-flex rounded-lg border border-border p-0.5">
          {(['month', 'range'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => setMode(m)}
              className={
                mode === m
                  ? 'rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium pointer-coarse:min-h-11 text-white dark:bg-slate-100 dark:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
                  : 'rounded-md px-3 py-1.5 text-sm pointer-coarse:min-h-11 text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60'
              }
            >
              {m === 'month' ? t('me.periodMonth') : t('me.periodRange')}
            </button>
          ))}
        </div>
        {mode === 'month' ? (
          <TextInput label={t('me.month')} type="month" value={monthPick} onChange={(e) => setMonthPick(e.target.value)} />
        ) : (
          <>
            <TextInput label={t('me.from')} type="month" value={fromPick} onChange={(e) => setFromPick(e.target.value)} />
            <TextInput label={t('me.to')} type="month" value={toPick} onChange={(e) => setToPick(e.target.value)} />
          </>
        )}
        <Button
          className="ml-auto"
          onClick={toggleShown}
          aria-pressed={shown}
          icon={shown ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        >
          {shown ? t('me.hideAmounts') : t('me.showAmounts')}
        </Button>
      </div>
      {backwards && (
        <div role="alert">
          <Notice>{t('me.badRange')}</Notice>
        </div>
      )}

      {isError && (
        <ErrorState
          title={t('me.payslipsLoadFailed')}
          error={error}
          onRetry={() => void refetch()}
          retrying={isFetching}
        />
      )}
      {isLoading && <LoadingState rows={4} />}

      {!isLoading && !isError && months.length === 0 && (
        // Said as a fact, not an error: nothing to show before a period is picked.
        <EmptyState title={period ? t('me.noneInPeriod') : t('me.pickPeriod')} />
      )}

      {months.length > 0 && (
        // No `cardFooter`: the amount in each run column is the control that opens it.
        <div className="list-viewport list-sticky-head">
          <ResponsiveTable
            columns={monthColumns}
            rows={sortedMonths}
            rowKey={(row) => monthLabel(row.year, row.month)}
          />
        </div>
      )}
      {months.length > 0 && !shown && <p className={mutedTextClass}>{t('me.amountsHidden')}</p>}
      {open && <PayslipDialog slip={open} onClose={() => setOpen(null)} />}

      <MyOneOffPayslips period={period} shown={shown} />

      <MyLeavePayoutHistory />
    </div>
  )
}

function Line({
  label,
  value,
  strong,
  noWrap,
}: {
  label: string
  value: string
  strong?: boolean
  noWrap?: boolean
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd
        className={`${noWrap ? 'whitespace-nowrap ' : ''}tabular-nums ${
          strong
            ? 'font-semibold text-slate-900 dark:text-slate-100'
            : 'text-slate-700 dark:text-slate-200'
        }`}
      >
        {value}
      </dd>
    </div>
  )
}

function PayslipDialog({ slip, onClose }: { slip: MyPayslip; onClose: () => void }) {
  const { i18n } = useTranslation()
  const [lang, setLang] = useState<PayslipLang>(() => defaultPayslipLang(i18n.resolvedLanguage ?? i18n.language))
  // A clone, so the toggle re-labels this dialog without changing the app's language.
  const payslipI18n = useMemo(() => i18n.cloneInstance({ lng: lang }), [i18n, lang])
  return (
    <I18nextProvider i18n={payslipI18n}>
      <PayslipDialogBody slip={slip} onClose={onClose} lang={lang} onLang={setLang} />
    </I18nextProvider>
  )
}

function PayslipDialogBody({
  slip,
  onClose,
  lang,
  onLang,
}: {
  slip: MyPayslip
  onClose: () => void
  lang: PayslipLang
  onLang: (lang: PayslipLang) => void
}) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const { data, isLoading, isError, error, isFetching, refetch } = useMyPayslip(
    slip.year,
    slip.month,
    slip.run as PayRun,
    true,
  )
  // This slip's own month decides it, not `data`, so the dialog hides the same lines before the detail fetch resolves.
  const grossOnly = isGrossOnlyPeriod(slip.year, slip.month) || netNotRecorded(slip)
  const yearEnd = data ? clYearEndLine(data) : null

  const sections: Record<PayslipSection, ReactNode> | null = data
    ? {
        work: (
          <>
            {/* How much work this figure is built on -- across the top as a
                row of tiles rather than as stacked lines. These four are one
                fact about the month and belong on one line of it. */}
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label={t('payroll.reportedHours')} value={String(Number(data.total_hours))} />
              <Stat label={t('payroll.shifts')} value={String(Number(data.shift_count))} />
              {data.included_days > 0 && <Stat label={t('payroll.includedDays')} value={String(data.included_days)} />}
              <Stat label={t('payroll.alDays')} value={String(Number(data.al_days))} />
            </dl>
          </>
        ),
        segments: (
          <>
            {/* The derivation: the rates this slip was actually issued at,
                frozen at issue time -- not a recalculation against today's
                rates. See `FrozenSegment` and the router's `rate_breakdown`. */}
            <div className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700">
              <div className="flex items-center gap-1">
                <p className={mutedTextClass}>
                  {t('payroll.socialTag', { tag: data.soc_code || '—' })}
                </p>
                {data.rate_breakdown != null && (
                  <HelpTip label={t('payroll.socialTag', { tag: data.soc_code || '—' })}>
                    {t('payroll.frozenAtIssue')}
                  </HelpTip>
                )}
              </div>
              {data.rate_breakdown == null ? (
                <p className={mutedTextClass}>{t('payroll.noFrozenBreakdown')}</p>
              ) : (
                <>
                  {data.rate_breakdown.map((segment, i) => (
                    <FrozenSegmentRow key={`${segment.start}-${i}`} segment={segment} />
                  ))}
                </>
              )}
            </div>
          </>
        ),
        annualLeave: (
          <>
            {/* Leave: a day count is not currency, so the two are shown apart.
                `leaveTopUp` already spells out days x factor, so this is the
                whole of "how the AL got calculated" -- it needed width, not
                more fields. */}
            {Number(data.al_days) !== 0 || Number(data.leave_value) !== 0 ? (
              <dl className="grid gap-x-6 gap-y-1.5 border-t border-slate-200 pt-3 text-sm sm:grid-cols-2 dark:border-slate-700">
                <Line
                  label={t('payroll.leaveTopUp', {
                    days: Number(data.al_days),
                    factor: formatMoney(Number(data.al_factor)),
                  })}
                  value={
                    grossOverridden(data)
                      ? t('payroll.notApplied', { amount: formatMoney(Number(data.leave_value)) })
                      : formatMoney(Number(data.leave_value))
                  }
                />
                {data.al_paid_days && data.al_paid_days.length > 0 && (
                  <Line label={t('payroll.alPaidDates')} value={data.al_paid_days.join(', ')} />
                )}
              </dl>
            ) : (
              <p className={`border-t border-slate-200 pt-2 dark:border-slate-700 ${mutedTextClass}`}>
                {t('payroll.leaveInFixed')}
              </p>
            )}
          </>
        ),
        totals: (
          <>
            {/* The ledger, and the allowance itemisation beside it instead of
                stacked above it. The ledger is a document and stays a single
                column -- what wasted the width was everything else sharing
                that column. Its `allowances` line is the subtotal of the panel
                to its right; the two carry the same label, so they still read
                as one figure and its parts. */}
            <div className="grid gap-4 border-t border-slate-200 pt-3 lg:grid-cols-2 dark:border-slate-700">
              <dl className="space-y-1.5 text-sm">
                {/* Allowances, bonus, deduction, then gross -- the
                    deduction is a PART of gross, so it sits above it; after
                    gross it read as a second deduction. With `custom_gross`
                    set it ends on ONE gross, labelled as set by payroll, and
                    bonus/deduction read "not applied".
                    @see grossLedger */}
                {grossLedger(data).map((line) => (
                  <Line
                    key={line.key}
                    label={
                      line.key === 'breakClPsl'
                        ? labelWithNote(grossLedgerLabel(line.key, t), payNoteText(data.break_cl_psl_note, data.break_cl_psl_note_parts, t))
                        : grossLedgerLabel(line.key, t)
                    }
                    value={
                      line.applied
                        ? `${line.sign === '−' ? '− ' : ''}${formatMoney(line.amount)}`
                        : t('payroll.notApplied', { amount: formatMoney(line.amount) })
                    }
                    strong={line.strong}
                  />
                ))}
                {/* Before the boundary, social/tax/net are hidden rather
                    than shown as an unreliable figure -- the row keeps its
                    gross, allowances and bonus, just not these three -- nor
                    the payable built from them.
                    @see isGrossOnlyPeriod */}
                {!grossOnly && (
                  <>
                    <Line
                      label={t('payroll.socialAt', { percent: Number(data.soc_percent) * 100 })}
                      value={`− ${formatMoney(Number(data.social))}`}
                    />
                    <Line
                      label={t('payroll.taxAt', { percent: Number(data.tax_percent) * 100 })}
                      value={`− ${formatMoney(Number(data.tax))}`}
                    />
                    <Line label={t('payroll.net')} value={formatMoney(Number(data.net))} strong />
                  </>
                )}
                {/* Everything from here to the bottom line is built
                    from `net` -- the advance paid off a final, the two
                    adjustments and the payable itself -- so a gross-only slip
                    hides it with the net, and the slip ends on its gross. */}
                {!grossOnly && slip.run === 'final' && (
                  <Line
                    label={t('payroll.advancePaid')}
                    value={`− ${formatMoney(Number(data.advance_paid))}`}
                  />
                )}
                {/* A bonus paid with the
                    advance is inside the deduction above, so without this the final
                    simply looked smaller than it should and nothing said why. */}
                {!grossOnly && Number(data.advance_adjustment) !== 0 && (
                  <Line
                    label={t('payroll.advanceAdjustment')}
                    value={`+ ${formatMoney(Number(data.advance_adjustment))}`}
                  />
                )}
                {/* The one post-tax figure an administrator can set.
                    The e-mailed payslip already states it, so this screen must
                    agree with it -- signed, same as there. */}
                {!grossOnly && Number(data.net_adjustment) !== 0 && (
                  <Line
                    label={t('payroll.netAdjustment')}
                    value={`${Number(data.net_adjustment) > 0 ? '+' : '−'} ${formatMoney(Math.abs(Number(data.net_adjustment)))}`}
                  />
                )}
                {!grossOnly && yearEnd && (
                  <Line
                    label={labelWithNote(t('payroll.clYearEndPay'), payNoteText(data.cl_year_end_note, data.cl_year_end_note_parts, t))}
                    value={clYearEndValue(yearEnd, formatMoney)}
                    noWrap
                  />
                )}
                {!grossOnly && afterTaxLines(data).map((line) => (
                  <Line key={line.key} label={afterTaxLabel(line.key, t)} value={`${line.sign} ${formatMoney(line.amount)}`} noWrap />
                ))}
                {!grossOnly && (
                  <div className="flex justify-between gap-3 border-t border-slate-200 pt-2 text-base font-semibold dark:border-slate-700">
                    {/* Named the way the payroll screen names the same number, so a
                        question about it does not start with two different words for
                        one figure. */}
                    <dt className="text-slate-700 dark:text-slate-200">
                      {t(slip.run === 'advance' ? 'payroll.advance' : 'payroll.remainder')}
                    </dt>
                    <dd className="tabular-nums text-slate-900 dark:text-slate-100">
                      {formatMoney(Number(data.payable))}
                    </dd>
                  </div>
                )}
                {data.note && (
                  <p className="pt-2 text-xs text-slate-500 dark:text-slate-400">{data.note}</p>
                )}
              </dl>

              <AllowanceBreakdown segments={data.rate_breakdown} overridden={grossOverridden(data)} />
            </div>
          </>
        ),
        notPaid: (
          <>
            {notPaidSegments(data.rate_breakdown).length > 0 && (
              <div className="space-y-2 border-t border-slate-200 pt-3 text-xs dark:border-slate-700">
                <p className="font-medium text-slate-600 dark:text-slate-300">
                  {t('payroll.excludedDays')}
                </p>
                {notPaidSegments(data.rate_breakdown).map((segment, i, all) => (
                  <div key={`${segment.start}-${i}`}>
                    {all.length > 1 && (
                      <p className={mutedTextClass}>
                        {segment.start} – {segment.end}
                      </p>
                    )}
                    <ExcludedDays state={recordedList(segment.excluded_days)} />
                  </div>
                ))}
              </div>
            )}
          </>
        ),
      }
    : null

  return (
    <ModalBackdrop onClose={onClose} aria-label={t('payroll.viewPayslip')}>
      <div className={dialogScrollPanelClass('3xl')}>
        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 px-5 pb-3 pt-5 sm:flex">
          <div className="min-w-0 sm:flex-1">
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">
              {monthLabel(slip.year, slip.month)} · {t(`payroll.run_${slip.run}`)}
            </h2>
            {data && (
              <p className={mutedTextClass}>
                {t('payroll.covers', { start: data.covers.start, end: data.covers.cut_off })}
              </p>
            )}
          </div>
          <div className="col-span-2 row-start-2 flex flex-wrap items-center justify-between gap-2 sm:contents">
            {/* NOT an `<a href>`, however much it looks like one should work.
                The bearer token is attached by the axios interceptor and lives in
                memory, not in a cookie, so a link navigation carries no
                credentials and the request 401s. `downloadFile` fetches the bytes
                through axios and hands them over as a blob -- see the docstring
                on `lib/download.ts`, which was written after this exact mistake. */}
            <PayslipLangPicker lang={lang} onLang={onLang} />
            <Button
              size="sm"
              className="pointer-coarse:min-h-11"
              loading={saving}
              icon={<Download className="h-3.5 w-3.5" />}
              onClick={async () => {
                setSaving(true)
                try {
                  await downloadFile(
                    myPayslipPdfUrl(slip.year, slip.month, slip.run as PayRun, lang),
                    `payslip-${slip.year}-${String(slip.month).padStart(2, '0')}-${slip.run}.pdf`,
                  )
                } catch (e) {
                  toast.error(`${t('me.pdfFailed')} ${errorMessage(e)}`)
                } finally {
                  setSaving(false)
                }
              }}
            >
              {t('action.download')}
            </Button>
          </div>
          <IconButton
            variant="secondary"
            className="col-start-2 row-start-1"
            label={t('action.close')}
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          />
        </div>

        <div className={dialogScrollBodyClass}>
        {isLoading && <LoadingState rows={4} />}
        {isError && (
          <ErrorState
            title={t('me.payslipLoadFailed')}
            error={error}
            onRetry={() => void refetch()}
            retrying={isFetching}
          />
        )}

        {data && (
          <div className="space-y-4">
            {sections &&
              PAYSLIP_SECTIONS.map((section) => <Fragment key={section}>{sections[section]}</Fragment>)}
          </div>
        )}
        </div>
      </div>
    </ModalBackdrop>
  )
}

/** Three-state: `known: false` (issued before this shipped) reads differently from `empty: true` (genuinely nothing). @see Recorded */
function RecordedRow<T>({
  label,
  state,
  render,
}: {
  label: string
  state: Recorded<T>
  render: (value: T) => string
}) {
  const { t } = useTranslation()
  const cannotTell = !state.known || state.empty
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd
        className={`tabular-nums ${
          cannotTell
            ? 'italic text-slate-500 dark:text-slate-400'
            : 'text-right text-slate-700 dark:text-slate-200'
        }`}
      >
        {!state.known ? t('payroll.notRecorded') : state.empty ? t('payroll.none') : render(state.value)}
      </dd>
    </div>
  )
}

/** Dict version of RecordedRow. `labelFor` exists because `hour_detail` keys are machine names that must be translated, unlike the server-written labels elsewhere. */
function RecordedDict({
  title,
  state,
  labelFor,
}: {
  title: string
  state: Recorded<Record<string, string | number>>
  labelFor?: (key: string) => string
}) {
  const { t } = useTranslation()
  return (
    <div>
      <p className="font-medium text-slate-600 dark:text-slate-300">{title}</p>
      {!state.known ? (
        <p className="pl-2 italic text-slate-500 dark:text-slate-400">{t('payroll.notRecorded')}</p>
      ) : state.empty ? (
        <p className="pl-2 italic text-slate-500 dark:text-slate-400">{t('payroll.none')}</p>
      ) : (
        <dl className="mt-0.5">
          {Object.entries(state.value).map(([key, value]) => (
            <div key={key} className="flex justify-between gap-3 pl-2">
              <dt className="text-slate-500 dark:text-slate-400">
                {labelFor ? labelFor(key) : key}
              </dt>
              <dd className="tabular-nums text-slate-700 dark:text-slate-200">{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}


/**
 * The days a segment's span covered and did not pay for, grouped by reason (mostly weekends). `known: false` means
 * unrecorded, which must not read as "nothing was excluded". @see RecordedRow
 */
function ExcludedDays({ state }: { state: Recorded<ExcludedDay[]> }) {
  const { t } = useTranslation()
  if (!state.known) {
    return <p className="pl-2 italic text-slate-500 dark:text-slate-400">{t('payroll.notRecorded')}</p>
  }
  if (state.empty) {
    return <p className="pl-2 italic text-slate-500 dark:text-slate-400">{t('payroll.excludedNone')}</p>
  }

  // First-seen order is date order; a Map keeps it, an object would reorder numeric-looking keys.
  const groups = new Map<string, { reason: string; label: string | null; days: string[] }>()
  for (const item of state.value) {
    // The label is part of the key: two holidays in one span are two groups.
    const key = `${item.reason}\u0000${item.label ?? ''}`
    const found = groups.get(key)
    if (found) found.days.push(item.day)
    else groups.set(key, { reason: item.reason, label: item.label, days: [item.day] })
  }

  return (
    <dl className="mt-0.5 space-y-1">
      {[...groups.values()].map((group) => (
        <div key={`${group.reason}-${group.label ?? ''}`} className="pl-2">
          <div className="flex justify-between gap-3">
            {/* The reason is a code and is translated; the label is a string
                the server already wrote for a reader ('F - Field', 'AL -
                Annual Leave') and is not. `t()`'s second argument is the
                fallback, so a reason added server-side later shows its own
                code rather than vanishing. */}
            <dt className="text-slate-500 dark:text-slate-400">
              {t(`payroll.excluded_${group.reason}`, group.reason)}
              {group.label ? ` · ${group.label}` : ''}
            </dt>
            {/* A bare number, like `RecordedDict` beside it: the heading
                already says these are days, and a "{{count}} days" string
                would be this codebase's first plural key -- three locales of
                plural rules, one of which (Mongolian) does not distinguish
                them, for a word the reader can already see. */}
            <dd className="shrink-0 tabular-nums text-slate-600 dark:text-slate-300">
              {group.days.length}
            </dd>
          </div>
          <p className="text-slate-500 dark:text-slate-400">{group.days.join(', ')}</p>
        </div>
      ))}
    </dl>
  )
}

/** One rate period as frozen at issue, in three bands; no masking toggle since it is always the person's own pay. @see Stat */
/**
 * An advance run's ledger of days, not a rate period: it has no pay basis or rate, so show none rather than a 0.00 or a gap.
 * ⛔ No excluded-days block: a ledger-only segment has no span, so the difference is undefined.
 */
function LedgerOnlySegment({ segment }: { segment: FrozenSegment }) {
  const { t } = useTranslation()
  return (
    <section className="rounded-lg border border-dashed border-slate-300 p-3 text-xs dark:border-slate-600">
      <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
        {t('payroll.ledgerSegment')}
      </h3>
      <p className="mt-1 leading-snug text-slate-500 dark:text-slate-400">
        {t('payroll.ledgerSegmentWhy')}
      </p>
      <dl className="mt-3 space-y-0.5 border-t border-slate-200 pt-2 dark:border-slate-700">
        <RecordedRow
          label={t('payroll.daysPaid')}
          state={recordedList(segment.days)}
          render={(v) => v.join(', ')}
        />
        <RecordedRow
          label={t('payroll.shiftDaysPaid')}
          state={recordedList(segment.shift_days)}
          render={(v) => v.join(', ')}
        />
      </dl>
    </section>
  )
}

function FrozenSegmentRow({ segment }: { segment: FrozenSegment }) {
  const { t } = useTranslation()
  // Decided on the absence of a rate AND a span, never on `model == null`: some segments carry a null basis alongside a real rate. @see isLedgerOnly
  if (isLedgerOnly(segment)) return <LedgerOnlySegment segment={segment} />
  const hours = Number(segment.hours)
  const shifts = Number(segment.shifts)
  const credit = holidayCredit(segment)
  const modelLabel =
    segment.model != null ? t(`payroll.model${segment.model}`, segment.model) : t('payroll.notRecorded')
  const perLabel = segment.model != null ? t(`payroll.per${segment.model}`, '') : ''

  return (
    <section className="rounded-lg border border-dashed border-slate-300 p-3 text-xs dark:border-slate-600">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
          {modelLabel} · {formatMoneyOrDash(segment.rate)}
          <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">{perLabel}</span>
        </h3>
        <span className="tabular-nums text-slate-500 dark:text-slate-400">
          {segment.carry_over ? segment.start : `${segment.start} → ${segment.end}`}
        </span>
      </header>

      {segment.carry_over && (
        <p className="mt-2 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
          {t('payroll.carryOver')}
        </p>
      )}

      {/* What this segment paid. Hours and shifts are conditional because a
          basis has one or the other, never both -- a Daily segment prices
          shifts and its `hours` is zero by construction. */}
      <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {hours > 0 && <Stat label={t('payroll.reportedHours')} value={String(hours)} />}
        {shifts > 0 && <Stat label={t('payroll.shifts')} value={String(shifts)} />}
        <Stat label={t('payroll.segmentGross')} value={formatMoneyOrDash(segment.gross)} />
      </dl>

      {/* The working behind those figures: three small maps of label to number,
          which read as columns. Three-state throughout -- a row issued before a
          field shipped says so, rather than looking like a row with nothing to
          report. Holidays are not a fourth map: they are credited from the
          calendar (no time_day ever carries a 'Holiday' mode), and the
          credited dates are their own line below. */}
      <div className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
        <RecordedDict
          title={t('payroll.hoursByType')}
          state={recordedMap(segment.hour_detail)}
          // Machine keys; the fallback argument shows an unknown hour type's own key rather than nothing.
          labelFor={(key) => t(`payroll.hourType_${key}`, key)}
        />
        <RecordedDict title={t('payroll.workModeDays')} state={recordedMap(segment.mode_days)} />
        <RecordedDict title={t('payroll.leaveBreakdown')} state={recordedMap(segment.leave_detail)} />
      </div>

      {/* Said in words because the parts genuinely do not add up to the total:
          `hours` above is weighted and includes the paid-leave and holiday day
          credit, and these are the raw logged figures. An employee checking
          them against their own timesheet would otherwise read the difference
          as an error on the payslip. @see SegmentResult.hour_detail */}
      {segment.hour_detail != null && Object.keys(segment.hour_detail).length > 0 && (
        <p className="mt-2 leading-snug text-slate-500 dark:text-slate-400">
          {t('payroll.hoursWeighted')}
        </p>
      )}

      {/* The date lists last and full width: they wrap to several lines and
          would squeeze the columns above into nothing. */}
      <dl className="mt-3 space-y-0.5 border-t border-slate-200 pt-2 dark:border-slate-700">
        {/* The task row, when this segment recorded one. */}
        <TaskPayLines segment={segment} money={formatMoney} />
        <RecordedRow
          label={t('payroll.daysPaid')}
          state={recordedList(segment.days)}
          render={(v) => v.join(', ')}
        />
        <RecordedRow
          label={t('payroll.shiftDaysPaid')}
          state={recordedList(segment.shift_days)}
          render={(v) => v.join(', ')}
        />
        {/* The holiday credit, already inside the hours and gross
            above -- a line only when something was credited. */}
        {credit && (
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">{t('payroll.holidaysCredited')}</dt>
            <dd className="text-right tabular-nums text-slate-700 dark:text-slate-200">
              {credit.join(', ')}
            </dd>
          </div>
        )}
      </dl>

    </section>
  )
}
